import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import appointmentService from '../../../services/appointment.service';
import {
    getAppointmentStatusMeta,
    getPatientInitials,
} from './subparts/appointmentsQuery';
import AppointmentReminderPills from './AppointmentReminderPills';
import AppointmentActionsMenu from './AppointmentActionsMenu';

function AppointmentListItem({ appt, isSelected, onCheckChange, onStatusUpdated }) {
    const [updating, setUpdating] = useState(false);

    const patient = appt.patient || {};
    const doctor = appt.doctor || {};
    const statusMeta = getAppointmentStatusMeta(appt.isConfirmed);
    const initials = getPatientInitials(patient);

    const handleCheckbox = (event) => onCheckChange(appt.id, event.target.checked);

    const handleConfirmCancel = async (confirmed) => {
        try {
            setUpdating(true);
            await appointmentService.updateAppointmentStatus(appt.clinicId, appt.id, confirmed);
            onStatusUpdated?.();
        } catch (error) {
            console.error(error);
            toast.error('Failed to update appointment status.');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <tr className={`appointments-row appointments-row--${statusMeta.variant}`}>
            <td className="appointments-row__checkbox">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelected}
                    onChange={handleCheckbox}
                    aria-label={`Select appointment for ${patient.firstName || ''} ${patient.lastName || ''}`.trim()}
                />
            </td>

            <td>
                <div className="appointments-patient-cell">
                    <span className="appointments-patient-cell__avatar" aria-hidden="true">
                        {initials || <i className="bi bi-person-fill" />}
                    </span>
                    <div className="min-width-0">
                        <Link to={`/patient-files/${appt.patientId}`} className="appointments-patient-cell__name">
                            {patient.firstName} {patient.lastName}
                        </Link>
                        {patient.preferredName && (
                            <div className="appointments-patient-cell__preferred">{patient.preferredName}</div>
                        )}
                    </div>
                </div>
            </td>

            <td>
                <div className="appointments-time-cell">
                    <span className="appointments-time-cell__range">
                        {appt.startTime} – {appt.endTime}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-doctor-cell">
                    <span className="appointments-doctor-cell__name">
                        Dr. {doctor.firstName} {doctor.lastName}
                    </span>
                </div>
            </td>

            <td>
                <div className="appointments-contact-cell">
                    {patient.email ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-envelope" />
                            {patient.email}
                        </span>
                    ) : null}
                    {patient.cellNumber ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-phone" />
                            {patient.cellNumber}
                        </span>
                    ) : null}
                    {patient.phoneNumber ? (
                        <span className="appointments-contact-item">
                            <i className="bi bi-telephone" />
                            {patient.phoneNumber}
                        </span>
                    ) : null}
                    {!patient.email && !patient.cellNumber && !patient.phoneNumber ? (
                        <span className="text-muted small">—</span>
                    ) : null}
                </div>
            </td>

            <td>
                <AppointmentReminderPills appointmentStatus={appt.appointmentStatus} />
            </td>

            <td>
                <span className={`appointments-status-pill appointments-status-pill--${statusMeta.variant}`}>
                    <i className={`bi ${statusMeta.icon}`} />
                    {statusMeta.label}
                </span>
            </td>

            <td className="text-end">
                <div className="appointments-actions">
                    {appt.isConfirmed === 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleConfirmCancel(true)}
                            disabled={updating}
                            title="Confirm appointment"
                        >
                            <i className="bi bi-check2-circle" />
                        </button>
                    )}

                    <AppointmentActionsMenu
                        appt={appt}
                        updating={updating}
                        onConfirmCancel={handleConfirmCancel}
                    />
                </div>
            </td>
        </tr>
    );
}

export default AppointmentListItem;
